import React, { useState } from "react";
import dayjs from "dayjs";
import Skeleton from "../common/skeleton";
import EmptyState from "../common/empty_state";
import SectionError from "./section_error";
import { Confirm } from "../../utils/confirm";
import { showToast } from "../../utils/show_toast";

const STATUS_META = {
    pending: { label: "확정 대기", cls: "bg-warning-subtle text-warning" },
    confirmed: { label: "체결 완료", cls: "bg-gain-subtle text-gain" },
    skipped: { label: "건너뜀", cls: "bg-surface-elevated text-content-muted" },
};

const formatUsd = (n) =>
    n == null ? "-" : "$" + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatKrw = (n) => (n == null ? "-" : "₩" + Math.round(Number(n)).toLocaleString());
const formatQty = (n) => (n == null ? "-" : Number(n).toFixed(6));

/** pending 확정 시 est_* 값을 prefill로 채운 인라인 수정 폼. */
function ConfirmForm({ purchase, onCancel, onSave, saving }) {
    const [draft, setDraft] = useState({
        quantity: purchase.est_quantity != null ? String(purchase.est_quantity) : "",
        price_usd: purchase.est_price_usd != null ? String(purchase.est_price_usd) : "",
        amount_krw: purchase.amount_krw != null ? String(purchase.amount_krw) : "",
        usd_krw: purchase.est_usd_krw != null ? String(purchase.est_usd_krw) : "",
    });

    const handleSave = () => {
        const quantity = Number(draft.quantity);
        const priceUsd = Number(draft.price_usd);
        if (!Number.isFinite(quantity) || quantity <= 0) {
            showToast({ type: "error", message: "체결 수량을 입력해주세요." });
            return;
        }
        if (!Number.isFinite(priceUsd) || priceUsd <= 0) {
            showToast({ type: "error", message: "체결 단가를 입력해주세요." });
            return;
        }
        onSave({
            action: "confirm",
            quantity,
            price_usd: priceUsd,
            amount_krw: draft.amount_krw ? Math.round(Number(draft.amount_krw)) : undefined,
            usd_krw: draft.usd_krw ? Number(draft.usd_krw) : undefined,
        });
    };

    return (
        <div className="mt-2 rounded-lg border border-surface-border bg-surface-hover p-2.5">
            <div className="grid grid-cols-2 gap-2">
                <div>
                    <label htmlFor={`qty-${purchase.id}`} className="mb-1 block text-xs text-content-muted">수량</label>
                    <input
                        id={`qty-${purchase.id}`}
                        type="number"
                        step="0.000001"
                        value={draft.quantity}
                        onChange={(e) => setDraft((p) => ({ ...p, quantity: e.target.value }))}
                        className="w-full rounded-lg border border-surface-border bg-surface-card p-1.5 text-xs text-content outline-none focus:border-brand"
                    />
                </div>
                <div>
                    <label htmlFor={`price-${purchase.id}`} className="mb-1 block text-xs text-content-muted">체결 단가(USD)</label>
                    <input
                        id={`price-${purchase.id}`}
                        type="number"
                        step="0.0001"
                        value={draft.price_usd}
                        onChange={(e) => setDraft((p) => ({ ...p, price_usd: e.target.value }))}
                        className="w-full rounded-lg border border-surface-border bg-surface-card p-1.5 text-xs text-content outline-none focus:border-brand"
                    />
                </div>
                <div>
                    <label htmlFor={`amt-${purchase.id}`} className="mb-1 block text-xs text-content-muted">투입 원화</label>
                    <input
                        id={`amt-${purchase.id}`}
                        type="number"
                        value={draft.amount_krw}
                        onChange={(e) => setDraft((p) => ({ ...p, amount_krw: e.target.value }))}
                        className="w-full rounded-lg border border-surface-border bg-surface-card p-1.5 text-xs text-content outline-none focus:border-brand"
                    />
                </div>
                <div>
                    <label htmlFor={`fx-${purchase.id}`} className="mb-1 block text-xs text-content-muted">체결 환율</label>
                    <input
                        id={`fx-${purchase.id}`}
                        type="number"
                        step="0.01"
                        value={draft.usd_krw}
                        onChange={(e) => setDraft((p) => ({ ...p, usd_krw: e.target.value }))}
                        className="w-full rounded-lg border border-surface-border bg-surface-card p-1.5 text-xs text-content outline-none focus:border-brand"
                    />
                </div>
            </div>
            <div className="mt-2 flex justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="min-h-[44px] rounded-lg border border-surface-border px-3 text-xs text-content-secondary hover:text-content"
                >
                    취소
                </button>
                <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="min-h-[44px] rounded-lg bg-brand/20 border border-brand/30 px-3 text-xs font-medium text-brand-light hover:bg-brand/30 disabled:opacity-50"
                >
                    {saving ? "저장 중..." : "체결 확정 저장"}
                </button>
            </div>
        </div>
    );
}

/**
 * PurchaseTimeline — ⑥ 매수 타임라인.
 * trade_date DESC(백엔드 정렬, pending 우선) 그대로 렌더. pending은 est_* prefill 확정 폼을 지원한다.
 * readOnly=true면 체결 확정/건너뜀 조작 버튼을 렌더하지 않는다(대시보드 미국주식 탭의 뷰잉 전용 재사용, DAS-164).
 * onEdit/onDelete는 선택적(DAS-169) — confirmed 행 수정·삭제 진입점. 미전달 시 버튼을 숨겨 다른 호출부에 영향이 없다.
 */
function PurchaseTimeline({ purchases, loading, error, onRetry, onConfirm, onEdit, onDelete, readOnly = false, headerAction }) {
    const [editingId, setEditingId] = useState(null);
    const [savingId, setSavingId] = useState(null);

    if (loading) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
                <Skeleton variant="line" width="30%" height={14} />
                <Skeleton variant="line" count={4} height={40} className="mt-3" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
                <SectionError message="매수 기록을 불러오지 못했습니다." onRetry={onRetry} />
            </div>
        );
    }

    const rows = Array.isArray(purchases) ? purchases : [];

    const handleSave = (id, payload) => {
        setSavingId(id);
        Promise.resolve(onConfirm(id, payload))
            .then(() => setEditingId(null))
            .finally(() => setSavingId(null));
    };

    const handleSkip = async (purchase) => {
        const ok = await Confirm.show({
            title: "매수 건너뛰기",
            message: `${purchase.ticker} (${purchase.trade_date}) 예정 매수를 건너뛰시겠습니까?`,
            variant: "danger",
        });
        if (!ok) return;
        setSavingId(purchase.id);
        Promise.resolve(onConfirm(purchase.id, { action: "skip" })).finally(() => setSavingId(null));
    };

    const handleDelete = async (purchase) => {
        const ok = await Confirm.show({
            title: "매수 기록 삭제",
            message: `${purchase.ticker} (${purchase.trade_date}) 확정 매수 기록을 삭제하시겠습니까?`,
            variant: "danger",
        });
        if (!ok) return;
        setSavingId(purchase.id);
        Promise.resolve(onDelete(purchase.id)).finally(() => setSavingId(null));
    };

    return (
        <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-card">
            <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-semibold text-content-secondary">매수 타임라인</div>
                {headerAction}
            </div>

            {rows.length === 0 ? (
                <EmptyState
                    title="매수 기록이 없습니다."
                    description="적립 플랜을 등록하면 약정일에 예정 매수 건이 자동으로 생성됩니다."
                />
            ) : (
                <div className="mt-3 space-y-2">
                    {rows.map((p) => {
                        const meta = STATUS_META[p.status] || STATUS_META.confirmed;
                        const isPending = p.status === "pending";
                        const isEditing = editingId === p.id;
                        return (
                            <div
                                key={p.id}
                                className={`rounded-xl border p-3 ${isPending ? "border-warning/30 bg-warning-subtle" : "border-surface-border bg-surface-card-alt"}`}
                            >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <span className="font-num text-sm font-semibold text-content">{p.ticker}</span>
                                        <span className="text-xs text-content-muted">
                                            {dayjs(p.trade_date).format("YYYY-MM-DD")}
                                        </span>
                                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}>
                                            {meta.label}
                                        </span>
                                    </div>
                                    {isPending && !isEditing && !readOnly && (
                                        <div className="flex gap-2">
                                            <button
                                                type="button"
                                                onClick={() => setEditingId(p.id)}
                                                className="min-h-[44px] rounded-lg border border-brand/30 bg-brand/10 px-3 text-xs font-medium text-brand-light hover:bg-brand/20"
                                            >
                                                체결 확정
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleSkip(p)}
                                                disabled={savingId === p.id}
                                                className="min-h-[44px] rounded-lg border border-surface-border px-3 text-xs text-content-secondary hover:bg-surface-hover disabled:opacity-50"
                                            >
                                                건너뜀
                                            </button>
                                        </div>
                                    )}
                                    {p.status === "confirmed" && !readOnly && (onEdit || onDelete) && (
                                        <div className="flex gap-2">
                                            {onEdit && (
                                                <button
                                                    type="button"
                                                    aria-label={`${p.ticker} 매수 기록 수정`}
                                                    onClick={() => onEdit(p)}
                                                    disabled={savingId === p.id}
                                                    className="min-h-[44px] rounded-lg border border-surface-border bg-brand-subtle px-3 text-xs font-medium text-brand-light hover:bg-brand/20 disabled:opacity-50"
                                                >
                                                    수정
                                                </button>
                                            )}
                                            {onDelete && (
                                                <button
                                                    type="button"
                                                    aria-label={`${p.ticker} 매수 기록 삭제`}
                                                    onClick={() => handleDelete(p)}
                                                    disabled={savingId === p.id}
                                                    className="min-h-[44px] rounded-lg border border-surface-border px-3 text-xs text-content-secondary hover:bg-surface-hover disabled:opacity-50"
                                                >
                                                    삭제
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {!isPending && (
                                    <div className="mt-1.5 grid grid-cols-3 gap-x-2 text-xs text-content-secondary">
                                        <span>{formatQty(p.quantity)} 주</span>
                                        <span>{formatUsd(p.price_usd)}</span>
                                        <span className="text-right">{formatKrw(p.amount_krw)}</span>
                                    </div>
                                )}

                                {isPending && !isEditing && (p.est_price_usd != null || p.est_quantity != null) && (
                                    <div className="mt-1.5 text-xs text-content-muted">
                                        추정 체결가 {formatUsd(p.est_price_usd)} · 추정 수량 {formatQty(p.est_quantity)} · 추정 환율{" "}
                                        {p.est_usd_krw != null ? Number(p.est_usd_krw).toLocaleString() : "-"}
                                    </div>
                                )}

                                {isPending && isEditing && (
                                    <ConfirmForm
                                        purchase={p}
                                        onCancel={() => setEditingId(null)}
                                        onSave={(payload) => handleSave(p.id, payload)}
                                        saving={savingId === p.id}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default PurchaseTimeline;
